/**
 * External-facing single-order shell: header, regimen banner, side tabs,
 * patient/provider grids, and child routes (`Outlet`) for drugs / BO /
 * documents / audit trail.
 *
 * This component is a thin orchestrator. Data fetching, derived state,
 * tab generation, and modal lifecycle are owned by the hooks under
 * `./hooks`; presentation is split across the components under `./components`.
 *
 * @module singleOrderForExternal/singleOrderForExternalLayout
 */
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { LoaderMessage } from "../../components/loaderMessage/loaderMessage";
import { UserGeneralInfoContextKeys } from "../../enums/userGeneralInfoContextKeys";
import { UserStore } from "../../store/UserGeneralInfoStore";
import BackArrowBlue from "../../svg/back-arrow-blue";
import OrderHeaderBar from "./components/OrderHeaderBar";
import OrderModals from "./components/OrderModals";
import OrderSideDetailsPanel from "./components/OrderSideDetailsPanel";
import OrderSideNav from "./components/OrderSideNav";
import RegimenNameBanner from "./components/RegimenNameBanner";
import { useExternalOrderModals } from "./hooks/useExternalOrderModals";
import { useExternalOrderTabs } from "./hooks/useExternalOrderTabs";
import { useSingleOrderForExternal } from "./hooks/useSingleOrderForExternal";

/**
 * Loads order + audit overview, derives visible tabs, and lays out
 * navigation and panes for the external single-order view.
 *
 * @returns Loader while data is fetching, or the full external order chrome
 */
const SingleOrderForExternalLayout = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const {
    loading,
    generalOrderDetails,
    authOnFileDetails,
    coverageDetails,
    patientData,
    providerData,
  } = useSingleOrderForExternal(orderId);
  const tabSwitchElements = useExternalOrderTabs(authOnFileDetails);
  const modals = useExternalOrderModals();

  /** Navigates to the parent external worklist stored in {@link UserStore}. */
  const onBackClick = () => {
    navigate(
      `/external-worklist/${UserStore.getItem(UserGeneralInfoContextKeys.PARENT_TABLE) ?? ""}`,
    );
  };

  if (loading) {
    return (
      <div className="single-order-for-external--layout">
        <LoaderMessage message="Fetching Order Details..." />
      </div>
    );
  }

  return (
    <div className="single-order-for-external--layout">
      <div className="single-order-for-external--header" onClick={onBackClick}>
        <BackArrowBlue />
        Go Back
      </div>
      <div className="single-order-for-external--content">
        <div className="single-order-for-external--content--header">
          <OrderHeaderBar
            generalOrderDetails={generalOrderDetails}
            coverageDetails={coverageDetails}
            authOnFileDetails={authOnFileDetails}
          />
        </div>
        <RegimenNameBanner
          regimenName={generalOrderDetails?.order?.payload?.regimen_name}
          authOnFile={authOnFileDetails?.auth_on_file}
          clinical_document_path={
            generalOrderDetails?.order?.clinical_document?.file_path
          }
          onOpenImageViewer={modals.openImageViewer}
        />
        <div className="single-order-for-external--content--secondary">
          <div className="single-order-for-external--content--secondary__left">
            <OrderSideNav tabs={tabSwitchElements} />
            <div
              className="single-order-for-external--content--secondary__left--outlet min-h-0"
              style={{ overflow: "auto" }}
            >
              <Outlet />
            </div>
          </div>
          <OrderSideDetailsPanel
            patientData={patientData}
            providerData={providerData}
          />
        </div>
      </div>
      <OrderModals
        modals={modals}
        generalOrderDetails={generalOrderDetails}
        authOnFileDetails={authOnFileDetails}
      />
    </div>
  );
};

export default SingleOrderForExternalLayout;
